import { useDropzone, FileRejection } from "react-dropzone";
import { FaLock } from "react-icons/fa";
import { RiUploadCloud2Line, RiFolderOpenLine } from "react-icons/ri";
import { FileWithPath } from "./photoModel";

export const MAX_FILE_SIZE = 20_000_000; // 20 MB
export const ACCEPTED_IMAGE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

// Keeps S3 keys clean: filenames must be english/ascii only. Rejected files are
// surfaced with this reason so the user can rename and retry. Mirrors
// ImportPhoto/PhotoDropZone.
function nameValidator(file: File) {
  if (/\s/.test(file.name)) {
    return {
      code: "name-has-whitespace",
      message: "ชื่อไฟล์ต้องไม่มีช่องว่าง (space)",
    };
  }
  const english = /^[a-zA-Z0-9._-]+$/;
  if (!english.test(file.name)) {
    return {
      code: "name-not-in-english",
      message: "ชื่อไฟล์ต้องเป็นภาษาอังกฤษเท่านั้น",
    };
  }
  return null;
}

function BarcodeDropZone({
  onFilesAdded,
  disabled,
  compact,
}: {
  onFilesAdded: (accepted: FileWithPath[], rejections: FileRejection[]) => void;
  disabled: boolean;
  compact?: boolean;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    disabled,
    accept: ACCEPTED_IMAGE_TYPES,
    maxSize: MAX_FILE_SIZE,
    validator: nameValidator,
    // Use the classic <input> path so `webkitdirectory` works for the browse
    // fallback. Drag-and-drop of folders is handled by getFilesFromEvent
    // regardless of this flag, so multi-folder drop still works.
    useFsAccessApi: false,
    onDrop: (accepted, rejections) => {
      onFilesAdded(accepted as FileWithPath[], rejections);
    },
  });

  // Compact bar — shown once files are already on the board, so the big
  // illustrated zone doesn't dominate the screen (the batch summary is below).
  if (compact) {
    return (
      <div
        {...getRootProps({
          className: `dropzone group flex items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-2.5 text-center text-sm transition-colors ${
            disabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
              : isDragActive
              ? "border-blue-500 bg-blue-50 text-blue-600"
              : "cursor-pointer border-gray-300 bg-gray-50/60 text-gray-500 hover:border-blue-400 hover:bg-blue-50/50"
          }`,
        })}
      >
        <input
          {...getInputProps()}
          {...({ webkitdirectory: "", directory: "" } as any)}
        />
        <RiUploadCloud2Line size={18} className="shrink-0" />
        <span className="font-medium">
          {disabled
            ? "กำลังอัปโหลด…"
            : isDragActive
            ? "วางเพื่อเพิ่มโฟลเดอร์"
            : "ลากโฟลเดอร์เพิ่ม หรือคลิกเพื่อเลือก"}
        </span>
      </div>
    );
  }

  return (
    <div
      {...getRootProps({
        className: `dropzone group relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-6 text-center transition-all duration-200 ${
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50"
            : isDragActive
            ? "scale-[1.01] border-blue-500 bg-blue-50 shadow-md"
            : "cursor-pointer border-gray-300 bg-gray-50/60 hover:border-blue-400 hover:bg-blue-50/50"
        }`,
      })}
    >
      {/* webkitdirectory makes the browse picker select one folder per click */}
      <input
        {...getInputProps()}
        {...({ webkitdirectory: "", directory: "" } as any)}
      />
      {disabled ? (
        <>
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-200">
            <FaLock size={26} className="fill-gray-500" />
          </div>
          <span className="font-medium text-gray-600">
            กำลังอัปโหลด… (ล็อกอยู่)
          </span>
        </>
      ) : (
        <>
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              isDragActive
                ? "bg-blue-500 text-white"
                : "bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white"
            }`}
          >
            <RiUploadCloud2Line size={20} />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-700">
              {isDragActive ? "วางเพื่อเพิ่มโฟลเดอร์" : "ลากโฟลเดอร์มาวางที่นี่"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              โครงสร้าง{" "}
              <span className="font-medium text-gray-600">
                Barcode / Mat Code / cover|packshot
              </span>{" "}
              · วางพร้อมกันหลาย Barcode ได้
            </p>
          </div>

          {/* Folder-structure example (text tree — mirrors the required layout) */}
          <pre className="px-4 py-3 overflow-x-auto text-left border border-gray-200 rounded-lg bg-white text-[11px] leading-relaxed text-gray-600">
{`8851989064178/            ← barcode
└─ 22C-0798/              ← matcode
   ├─ cover/
   │   └─ 8851989064178_22C-0798_1_F.png
   └─ packshot/
       ├─ 8851989064178_22C-0798_1_F.png
       ├─ 8851989064178_22C-0798_2_F.png
       └─ 8851989064178_22C-0798_B.png`}
          </pre>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors group-hover:border-blue-400 group-hover:text-blue-600">
            <RiFolderOpenLine size={15} /> หรือคลิกเพื่อเลือกโฟลเดอร์
          </span>
          <p className="text-xs text-gray-400">
            รองรับ .jpg .jpeg .png · ไม่เกิน 20&nbsp;MB ต่อไฟล์
          </p>
        </>
      )}
    </div>
  );
}

export default BarcodeDropZone;
